import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../api/client';
import type { ProjectStatus, ProjectSummaryDTO } from '@captionflow/shared';
import { Logo } from '../components/Logo';
import { NewProjectDialog } from '../components/NewProjectDialog';
import '../styles/dashboard.css';

/** "3m ago", "2h ago", "5d ago", else a short date. */
function relTime(iso: string): string {
  const then = new Date(iso).getTime();
  const s = Math.max(0, (Date.now() - then) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function fmtDur(ms: number | null): string | null {
  if (!ms) return null;
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** The video's REAL aspect ratio from its dimensions, snapped to a common label
 *  (9:16, 16:9, 1:1, 4:5, …). Falls back to the project's target when unknown. */
function aspectLabel(width: number | null, height: number | null, fallback: string): string {
  if (!width || !height) return fallback;
  const r = width / height;
  const common: [string, number][] = [
    ['9:16', 9 / 16], ['16:9', 16 / 9], ['1:1', 1], ['4:5', 4 / 5], ['5:4', 5 / 4],
    ['3:4', 3 / 4], ['4:3', 4 / 3], ['2:3', 2 / 3], ['3:2', 3 / 2], ['21:9', 21 / 9],
  ];
  let bestLabel = '9:16';
  let bestVal = 9 / 16;
  let bestDiff = Infinity;
  for (const [label, val] of common) {
    const d = Math.abs(val - r);
    if (d < bestDiff) { bestDiff = d; bestLabel = label; bestVal = val; }
  }
  // Within ~4% of a common ratio → use its clean label; otherwise reduce exactly.
  if (bestDiff / bestVal <= 0.04) return bestLabel;
  const gcd = (a: number, b: number): number => (b ? gcd(b, a % b) : a);
  const g = gcd(width, height) || 1;
  return `${Math.round(width / g)}:${Math.round(height / g)}`;
}

function statusInfo(status: ProjectStatus): { label: string; cls: string } {
  switch (status) {
    case 'ready':
      return { label: 'Ready', cls: 'ready' };
    case 'processing':
      return { label: 'Processing', cls: 'processing' };
    case 'failed':
      return { label: 'Failed', cls: 'failed' };
    default:
      return { label: 'Draft', cls: 'draft' };
  }
}

/** Small inline status glyph: a tick for ready, else a dot (spins for processing). */
function StatusIcon({ status }: { status: ProjectStatus }) {
  if (status === 'ready') {
    return (
      <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden>
        <path d="M20 6L9 17l-5-5" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  return <span className="dash-status-dot" />;
}

function greeting(): string {
  const h = new Date().getHours();
  if (h < 5) return 'Working late';
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  if (h < 21) return 'Good evening';
  return 'Good night';
}

type Filter = 'all' | 'ready' | 'processing';

/**
 * Dashboard: recent projects in the shared Kaption brand theme.
 * Distinguishes three states:
 *  - not signed in (API returns 401) → prompt to sign in
 *  - API unreachable (network error, not an ApiClientError) → offline banner
 *  - signed in → the project list
 */
export function DashboardPage() {
  const queryClient = useQueryClient();
  const [loggingOut, setLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState<string | null>(null);
  const logout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    setLogoutError(null);
    try {
      await api.logout();
      await queryClient.cancelQueries();
      queryClient.clear();
      window.location.replace('/login');
    } catch {
      setLogoutError('Could not log out. Please try again.');
      setLoggingOut(false);
    }
  };
  const { data, error, isLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.listProjects(),
    retry: false,
    // While any project is still processing, poll so its status flips to Ready live.
    refetchInterval: (q) =>
      (q.state.data?.projects ?? []).some((p) => p.status === 'processing') ? 4000 : false,
  });

  const [filter, setFilter] = useState<Filter>('all');
  const [q, setQ] = useState('');

  // New-project dialog. `/new` redirects to `/?new=1`, so honour that param and
  // keep it in sync with the URL (shareable, back-button closes the dialog).
  const [params, setParams] = useSearchParams();
  const showNew = params.get('new') === '1';
  const openNew = () => setParams((p) => {
    p.set('new', '1');
    return p;
  });
  const closeNew = () => setParams(
    (p) => {
      p.delete('new');
      return p;
    },
    { replace: true },
  );

  const unauthorized = error instanceof ApiClientError && error.status === 401;
  // A genuine connectivity problem: fetch threw (TypeError), not a normal HTTP
  // response. An ApiClientError means the API answered - so it IS connected.
  const apiDown = Boolean(error) && !(error instanceof ApiClientError);

  const all = data?.projects ?? [];
  const counts = useMemo(
    () => ({
      all: all.length,
      ready: all.filter((p) => p.status === 'ready').length,
      processing: all.filter((p) => p.status === 'processing').length,
    }),
    [all],
  );

  const projects = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return all.filter((p) => {
      if (filter === 'ready' && p.status !== 'ready') return false;
      if (filter === 'processing' && p.status !== 'processing') return false;
      if (needle && !p.name.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [all, filter, q]);

  return (
    <div className="dash">
      <header className="dash-top">
        <div className="dash-brand">
          <Logo size={18} />
        </div>
        <div className="dash-top-actions">
          {unauthorized ? (
            <Link to="/login" className="dash-btn ghost">
              Sign in
            </Link>
          ) : (
            <Link to="/billing" className="dash-btn ghost">
              Billing
            </Link>
          )}
          <button type="button" className="dash-btn primary" onClick={openNew}>
            <span className="dash-plus">+</span> New project
          </button>
          {!unauthorized && (
            <button type="button" className="dash-btn ghost" onClick={logout} disabled={loggingOut}>
              {loggingOut ? 'Logging out…' : 'Log out'}
            </button>
          )}
        </div>
      </header>

      <main className="dash-main">
        {logoutError && <div className="dash-banner" role="alert">{logoutError}</div>}
        {apiDown && (
          <div className="dash-banner">
            Can't reach the API - you can still open the <Link to="/editor">demo editor</Link> with
            the built-in transcript.
          </div>
        )}

        {unauthorized && (
          <div className="dash-banner">
            You're not signed in. <Link to="/login">Sign in or create an account</Link> to save
            projects - or jump straight into the <Link to="/editor">demo editor</Link>.
          </div>
        )}

        <div className="dash-head">
          <div className="dash-head-title">
            <h1>{greeting()}</h1>
            {!isLoading && !error && (
              <span className="dash-count">{counts.all} project{counts.all === 1 ? '' : 's'}</span>
            )}
          </div>
          {counts.all > 0 && (
            <div className="dash-controls">
              <div className="dash-search">
                <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden>
                  <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="2" />
                  <path d="M20 20l-3.2-3.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search projects"
                  aria-label="Search projects"
                />
              </div>
              <div className="dash-seg" role="tablist" aria-label="Filter projects">
                {(['all', 'ready', 'processing'] as Filter[]).map((f) => (
                  <button
                    key={f}
                    role="tab"
                    aria-selected={filter === f}
                    className={filter === f ? 'on' : ''}
                    onClick={() => setFilter(f)}
                  >
                    {f === 'all' ? 'All' : f === 'ready' ? 'Ready' : 'Processing'}
                    <span className="dash-seg-n">{counts[f]}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {isLoading && (
          <div className="dash-grid">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="dash-card skeleton">
                <div className="dash-thumb" />
                <div className="dash-meta">
                  <div className="sk-line w70" />
                  <div className="sk-line w40" />
                </div>
              </div>
            ))}
          </div>
        )}

        {!isLoading && (
          <div className="dash-grid">
            <button type="button" className="dash-newcard" onClick={openNew}>
              <span className="dash-newcard-plus">+</span>
              <strong>New project</strong>
              <small>Upload a video to caption</small>
            </button>

            {projects.map((p: ProjectSummaryDTO) => {
              const st = statusInfo(p.status);
              const dur = fmtDur(p.durationMs);
              return (
                <Link key={p.id} to={`/studio/${p.id}`} className="dash-card">
                  <div className="dash-thumb-box">
                    <div
                      className="dash-thumb"
                      style={{ backgroundImage: p.thumbnailUrl ? `url(${p.thumbnailUrl})` : undefined }}
                    >
                      {!p.thumbnailUrl && <span className="dash-thumb-fallback">{p.name.slice(0, 1).toUpperCase()}</span>}
                      {dur && <span className="dash-dur">{dur}</span>}
                    </div>
                  </div>
                  <div className="dash-meta">
                    <strong title={p.name}>{p.name}</strong>
                    <div className="dash-times">
                      <span className={`dash-status ${st.cls}`}>
                        <StatusIcon status={p.status} />
                        {st.label}
                      </span>
                      <span className="dash-dot-sep">·</span>
                      <span title={new Date(p.updatedAt).toLocaleString()}>Edited {relTime(p.updatedAt)}</span>
                      <span className="dash-tag">{aspectLabel(p.width, p.height, p.aspectRatio)}</span>
                    </div>
                  </div>
                </Link>
              );
            })}

            {!error && counts.all > 0 && projects.length === 0 && (
              <p className="dash-empty">No projects match your search.</p>
            )}
          </div>
        )}
      </main>

      <NewProjectDialog open={showNew} onClose={closeNew} />
    </div>
  );
}
